import { Signal, computed, inject } from '@angular/core';
import { Modulo } from '../../../core/models/auth.models';
import { AuthService } from '../../../core/services/auth.service';
import { RADA_PERMISOS, RadaPermiso } from '../rada.constants';

/**
 * Permisos por acción de RADA (R9.1). `AuthService.tieneServicio` solo compara ids numéricos, así
 * que aquí se lee el árbol del login (`Modulo[]` → `menu[]` → `servicios[]`) por `serv_nombre`.
 * No se modifica `AuthService`. Mecanismo confirmado por el PM (D2, 2026-10-29).
 * El backend sigue siendo quien autoriza: ocultar un botón es solo UX.
 */

/** Nombres (`serv_nombre`) de todos los servicios del árbol de permisos del usuario. */
export function nombresDeServicio(modulos: readonly Modulo[] | null | undefined): Set<string> {
  const nombres = new Set<string>();
  for (const modulo of modulos ?? []) {
    for (const item of modulo.menu ?? []) {
      for (const servicio of item.servicios ?? []) {
        if (servicio.serv_nombre) nombres.add(servicio.serv_nombre);
      }
    }
  }
  return nombres;
}

/** true si el árbol de permisos incluye el servicio `RADA_*` indicado. */
export function tienePermisoRada(
  modulos: readonly Modulo[] | null | undefined,
  permiso: RadaPermiso,
): boolean {
  return nombresDeServicio(modulos).has(permiso);
}

/**
 * true si el usuario tiene `RADA_TV_VER` y ningún otro servicio (R9.3: el usuario de TV solo ve
 * el Modo pantalla). Es el perfil «solo `RADA_TV_VER`» de 03A T-37. Un usuario con `RADA_TV_VER`
 * más cualquier otro permiso NO es solo-TV: conserva el shell y el menú.
 */
export function esUsuarioSoloTv(modulos: readonly Modulo[] | null | undefined): boolean {
  const nombres = nombresDeServicio(modulos);
  return nombres.size === 1 && nombres.has(RADA_PERMISOS.TV_VER);
}

/** Permisos RADA del usuario en sesión, como signals para los templates. */
export interface RadaPermisosUsuario {
  /** `RADA_PROD_VER`: Productividad, Dashboard, lectura de Metas y botón de Configuración de metas. */
  readonly puedeVerProductividad: Signal<boolean>;
  /** `RADA_METAS_EDITAR`: botones de edición de Metas, reglas y desactivar. */
  readonly puedeEditarMetas: Signal<boolean>;
  /** `RADA_ACT_RECALCULAR`: recálculo manual de activaciones. */
  readonly puedeRecalcular: Signal<boolean>;
  /** `RADA_TV_VER`: Modo pantalla. */
  readonly puedeVerTv: Signal<boolean>;
  /** `RADA_TV_CONFIG`: configuración de la secuencia del TV. */
  readonly puedeConfigurarTv: Signal<boolean>;
  /** Solo tiene `RADA_TV_VER` (usuario de TV). */
  readonly soloTv: Signal<boolean>;
}

/** Lee `auth.permisos()`. Se llama en contexto de inyección (campo de un componente o facade). */
export function injectRadaPermisos(): RadaPermisosUsuario {
  const auth = inject(AuthService);
  const nombres = computed(() => nombresDeServicio(auth.permisos()));
  return {
    puedeVerProductividad: computed(() => nombres().has(RADA_PERMISOS.PROD_VER)),
    puedeEditarMetas: computed(() => nombres().has(RADA_PERMISOS.METAS_EDITAR)),
    puedeRecalcular: computed(() => nombres().has(RADA_PERMISOS.ACT_RECALCULAR)),
    puedeVerTv: computed(() => nombres().has(RADA_PERMISOS.TV_VER)),
    puedeConfigurarTv: computed(() => nombres().has(RADA_PERMISOS.TV_CONFIG)),
    soloTv: computed(() => esUsuarioSoloTv(auth.permisos())),
  };
}
